import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PasswordIcon from '@mui/icons-material/Password';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api';
import { useAuthToken, useCurrentUser } from '../auth';
import { ROLE_LABEL_KEY_MAP } from '../constants/roles';
import {
  ORGANIZATION_CREATE_USER_PERMISSION,
  ORGANIZATION_DELETE_USER_PERMISSION,
  ORGANIZATION_RESEND_USER_VERIFICATION_EMAIL_PERMISSION,
  ORGANIZATION_SEND_USER_PASSWORD_RESET_PERMISSION,
} from '../constants/permissions';
import PageHeader from '../components/PageHeader';

interface OrganizationUser {
  id: string;
  email: string | null;
  roles: string[];
  sub?: string | null;
  displayName?: string | null;
  emailIsVerified?: boolean | null;
  passwordChangeRequired?: boolean | null;
}

const OrganizationUsers: React.FC = () => {
  const { t } = useTranslation();
  const api = useApi();
  const token = useAuthToken();
  const apiRef = useRef(api);
  const { impersonatedOrgId, permissions, currentUser } = useCurrentUser();
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const canCreateUser = permissions.includes(ORGANIZATION_CREATE_USER_PERMISSION);
  const canResendVerification = permissions.includes(ORGANIZATION_RESEND_USER_VERIFICATION_EMAIL_PERMISSION);
  const canSendPasswordReset = permissions.includes(ORGANIZATION_SEND_USER_PASSWORD_RESET_PERMISSION);
  const canDeleteUser = permissions.includes(ORGANIZATION_DELETE_USER_PERMISSION);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<OrganizationUser | null>(null);
  const [actionMessageKey, setActionMessageKey] = useState<string | null>(null);

  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsUser, setActionsUser] = useState<OrganizationUser | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [formErrors, setFormErrors] = useState<{ givenName?: string; familyName?: string; email?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  const loadUsers = async () => {
    if (!impersonatedOrgId || !token) {
      setUsers([]);
      setErrorKey(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorKey(null);

    try {
      const response = await apiRef.current.get('/users');
      setUsers(Array.isArray(response) ? response : []);
    } catch {
      setErrorKey('errors.fetchFailed');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    const run = async () => {
      await loadUsers();
      if (!active) return;
    };

    run();

    return () => {
      active = false;
    };
  }, [impersonatedOrgId, token]);

  const formatBoolean = (value: boolean | null | undefined) => {
    if (value === true) {
      return t('organizationUsers.booleanTrue');
    }
    if (value === false) {
      return t('organizationUsers.booleanFalse');
    }
    return t('organizationUsers.notAvailable');
  };

  const formatDeleteTarget = (user: OrganizationUser) => {
    const name = (user.displayName || '').trim();
    const emailText = (user.email || '').trim();
    if (name && emailText) return `${name} (${emailText})`;
    if (name) return name;
    if (emailText) return emailText;
    return user.id;
  };

  const isCurrentUserRow = (row: OrganizationUser) => {
    if (!currentUser?.sub || !row.sub) {
      return false;
    }
    return currentUser.sub === row.sub;
  };

  const closeActionsMenu = () => {
    setActionsAnchorEl(null);
    setActionsUser(null);
  };

  if (!impersonatedOrgId) {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <PageHeader title={t('organizationUsers.title')} />
        <Alert severity="info">{t('organizationUsers.noOrganization')}</Alert>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <PageHeader
        title={t('organizationUsers.title')}
        subtitle={t('organizationUsers.subtitle')}
        actions={
          canCreateUser ? (
            <Button variant="contained" onClick={() => setIsCreateOpen(true)}>
              {t('organizationUsers.addUser')}
            </Button>
          ) : undefined
        }
      />

      {errorKey && <Alert severity="error">{t(errorKey)}</Alert>}

      {actionMessageKey && (
        <Alert severity={actionMessageKey.includes('Success') ? 'success' : 'info'}>{t(actionMessageKey)}</Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('organizationUsers.columns.email')}</TableCell>
                <TableCell>{t('organizationUsers.columns.displayName')}</TableCell>
                <TableCell>{t('organizationUsers.columns.emailIsVerified')}</TableCell>
                <TableCell>{t('organizationUsers.columns.passwordChangeRequired')}</TableCell>
                <TableCell>{t('organizationUsers.columns.roles')}</TableCell>
                <TableCell align="right">{t('organizationUsers.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('organizationUsers.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const disabled = resendingUserId === user.id || resettingUserId === user.id || deletingUserId === user.id;
                  const canShowActions =
                    (canResendVerification && user.emailIsVerified === false) ||
                    (canSendPasswordReset && user.emailIsVerified === true) ||
                    (canDeleteUser && !isCurrentUserRow(user));

                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.email || t('organizationUsers.noEmail')}</TableCell>
                      <TableCell>{user.displayName || t('organizationUsers.notAvailable')}</TableCell>
                      <TableCell>{formatBoolean(user.emailIsVerified)}</TableCell>
                      <TableCell>{formatBoolean(user.passwordChangeRequired)}</TableCell>
                      <TableCell>
                        {user.roles.length > 0 ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {user.roles.map((role) => {
                              const translationKey = ROLE_LABEL_KEY_MAP[role];
                              const label = translationKey ? t(translationKey) : role;
                              return <Chip key={role} label={label} size="small" variant="outlined" />;
                            })}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('organizationUsers.noRoles')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {canShowActions && (
                          <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={(event) => {
                              setActionsAnchorEl(event.currentTarget);
                              setActionsUser(user);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl && actionsUser)}
        onClose={closeActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actionsUser && canResendVerification && actionsUser.emailIsVerified === false && (
          <MenuItem
            disabled={resendingUserId === actionsUser.id}
            onClick={async () => {
              setActionMessageKey(null);
              setResendingUserId(actionsUser.id);
              try {
                await api.post(`/users/${actionsUser.id}/email/resend`, {});
                setActionMessageKey('organizationUsers.messages.resendSuccess');
              } catch {
                setActionMessageKey('organizationUsers.messages.resendFailed');
              } finally {
                setResendingUserId(null);
                closeActionsMenu();
              }
            }}
          >
            <ListItemIcon>
              <MarkEmailReadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {resendingUserId === actionsUser.id
                ? t('organizationUsers.actions.resending')
                : t('organizationUsers.actions.resendVerification')}
            </ListItemText>
          </MenuItem>
        )}

        {actionsUser && canSendPasswordReset && actionsUser.emailIsVerified === true && (
          <MenuItem
            disabled={resettingUserId === actionsUser.id}
            onClick={async () => {
              setActionMessageKey(null);
              setResettingUserId(actionsUser.id);
              try {
                await api.post(`/users/${actionsUser.id}/password-reset`, {});
                setActionMessageKey('organizationUsers.messages.passwordResetSuccess');
              } catch {
                setActionMessageKey('organizationUsers.messages.passwordResetFailed');
              } finally {
                setResettingUserId(null);
                closeActionsMenu();
              }
            }}
          >
            <ListItemIcon>
              <PasswordIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {resettingUserId === actionsUser.id
                ? t('organizationUsers.actions.sendingReset')
                : t('organizationUsers.actions.passwordReset')}
            </ListItemText>
          </MenuItem>
        )}

        {actionsUser && canDeleteUser && !isCurrentUserRow(actionsUser) && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setUserToDelete(actionsUser);
              closeActionsMenu();
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>{t('organizationUsers.actions.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={isCreateOpen} onClose={() => !isSubmitting && setIsCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('organizationUsers.createTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(submitError)}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label={t('organizationUsers.fields.givenName')}
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              required
              error={Boolean(formErrors.givenName)}
              helperText={formErrors.givenName ? t(formErrors.givenName) : ' '}
              autoFocus
            />
            <TextField
              label={t('organizationUsers.fields.familyName')}
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              required
              error={Boolean(formErrors.familyName)}
              helperText={formErrors.familyName ? t(formErrors.familyName) : ' '}
            />
            <TextField
              label={t('organizationUsers.fields.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              error={Boolean(formErrors.email)}
              helperText={formErrors.email ? t(formErrors.email) : ' '}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateOpen(false)} disabled={isSubmitting}>
            {t('organizationUsers.createActions.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={isSubmitting}
            onClick={async () => {
              const errs: typeof formErrors = {};
              if (!givenName.trim()) errs.givenName = 'errors.required';
              if (!familyName.trim()) errs.familyName = 'errors.required';
              const emailTrimmed = email.trim();
              if (!emailTrimmed) {
                errs.email = 'errors.required';
              } else {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(emailTrimmed)) {
                  errs.email = 'organizationUsers.emailInvalid';
                }
              }
              setFormErrors(errs);
              if (Object.keys(errs).length > 0) return;

              setIsSubmitting(true);
              setSubmitError(null);
              try {
                await api.post('/users', {
                  givenName: givenName.trim(),
                  familyName: familyName.trim(),
                  email: emailTrimmed,
                });
                setIsCreateOpen(false);
                setGivenName('');
                setFamilyName('');
                setEmail('');
                setFormErrors({});
                await loadUsers();
              } catch {
                setSubmitError('errors.fetchFailed');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? t('organizationUsers.createActions.creating') : t('organizationUsers.createActions.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(userToDelete)}
        onClose={() => {
          if (!deletingUserId) setUserToDelete(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('organizationUsers.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('organizationUsers.deleteDialog.message', {
              user: userToDelete ? formatDeleteTarget(userToDelete) : '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserToDelete(null)} disabled={Boolean(deletingUserId)}>
            {t('organizationUsers.deleteDialog.no')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!userToDelete || Boolean(deletingUserId)}
            onClick={async () => {
              if (!userToDelete) return;
              setActionMessageKey(null);
              setDeletingUserId(userToDelete.id);
              try {
                await api.del(`/users/${userToDelete.id}`);
                setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
                setActionMessageKey('organizationUsers.messages.deleteSuccess');
                setUserToDelete(null);
              } catch {
                setActionMessageKey('organizationUsers.messages.deleteFailed');
              } finally {
                setDeletingUserId(null);
              }
            }}
          >
            {deletingUserId ? t('organizationUsers.deleteDialog.deleting') : t('organizationUsers.deleteDialog.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationUsers;
